var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
// External dependencies
import { Box, Typography, Divider, Paper, Grid, Avatar, LinearProgress, } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { appStyles } from '../App.styles';
import { axiosClient } from '../axios';
import { UserContext, getAuthHeader } from '../auth';
import { billingStyles } from './Billing.styles';
import { ENDPOINTS, LOG_LEVELS, SERVICES, CLIENT_ENDPOINTS } from '../constants';
import { SUBSCRIPTION_PLANS } from './constants';
;
export const Billing = function (props) {
    var _a, _b, _c, _d, _e, _f;
    const { t, i18n } = useTranslation();
    // Get user credential
    const user = useContext(UserContext);
    // Plan max usage
    const [planMaxUsage, setPlanMaxUsage] = useState({
        words: {
            basicModels: 0,
            proModels: 0,
        },
        images: {
            proModels: 0,
        },
    });
    // Subscription plan
    const [subscriptionPlan, setSubscriptionPlan] = useState(SUBSCRIPTION_PLANS.FREE);
    // Usage state
    const [usage, setUsage] = useState({
        words: {
            basicModels: 0,
            proModels: 0,
        },
        images: {
            proModels: 0,
        },
    });
    // Is billing customer created
    const [billingCustomerCreated, setBillingCustomerCreated] = useState(false);
    // Load usage
    useEffect(() => {
        const getUsage = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                if (!user) {
                    return;
                }
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                // Get current month, year
                const now = new Date();
                const month = now.getMonth() + 1;
                const year = now.getFullYear();
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get usage
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.USAGE}${ENDPOINTS.GET_USAGE}?month=${month}&year=${year}`, { headers });
                const { usage, planMaxUsage, subscriptionPlan, billingCustomerCreated } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                setUsage(usage);
                setPlanMaxUsage(planMaxUsage);
                setSubscriptionPlan(subscriptionPlan);
                setBillingCustomerCreated(billingCustomerCreated);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Billing, getUsage, ${err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.BILLING,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        getUsage();
    }, [user]);
    // Loading status of Manage button
    const [manageBillingLoading, setManageBillingLoading] = useState(false);
    const usagePercent = (usage, maxUsage) => {
        const result = (!usage || maxUsage === 'unlimit') ? 0 : (usage / maxUsage * 100);
        return clamp(result, 0, 100);
    };
    //Returns a number whose value is limited to the given range.
    function clamp(number, min, max) {
        return Math.max(min, Math.min(number, max));
    }
    const UsageItem = ({ usage, planMaxUsage, suffix, unit }) => {
        return _jsxs(_Fragment, { children: [_jsxs(Typography, Object.assign({ sx: billingStyles.usageText }, { children: [_jsx(Typography, Object.assign({ sx: billingStyles.usageNumberText }, { children: `${usage ? usage : '0'}${unit}` })), `${t('generated out of')}${(planMaxUsage !== 'unlimit') ? planMaxUsage : t("unlimit")} ${suffix}`] })), _jsx(LinearProgress, { sx: billingStyles.linearProgress, variant: "determinate", value: usagePercent(usage, planMaxUsage) })] });
    };
    /**
     * Get main plan name
     * @returns {String}
     */
    const getMainPlanName = () => {
        // Slit words in the subscription plan name
        const subscriptionPlanInArr = subscriptionPlan.split(' ');
        if (subscriptionPlanInArr.length <= 1) {
            // If empty or 1 word
            return subscriptionPlanInArr[0];
        }
        else {
            // If >= 2 words, chop monthly/yearly and return the rest
            return subscriptionPlanInArr.slice(1).join(' ');
        }
        ;
    };
    return (_jsxs(Box, Object.assign({ sx: {
            backgroundColor: 'transparent',
            overflow: 'auto',
            height: '100%',
        } }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { padding: '0 0 1.5rem', color: '#000000' }) }, { children: t('Current Plan') })), _jsx(Paper, Object.assign({ sx: {
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '1rem',
                    boxShadow: 'none',
                    backgroundColor: '#F8FAF7',
                } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { alignItems: 'stretch' }, columnSpacing: 2 }, { children: [_jsx(Grid, Object.assign({ item: true, sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                            } }, { children: _jsx(Avatar, Object.assign({ sx: {
                                    width: '2rem',
                                    height: '2rem',
                                    background: 'var(--sg-surfaces-light-surface-1, linear-gradient(0deg, rgba(0, 107, 84, 0.05) 0%, rgba(0, 107, 84, 0.05) 100%), #F8FAF7)',
                                } }, { children: "C" })) })), _jsxs(Grid, Object.assign({ item: true, xs: true }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { color: '#191C1B' }) }, { children: t(getMainPlanName()) })), _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { textAlign: 'left', color: '#3F4945' }) }, { children: t('Take your content game to the next level today!') }))] })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(LoadingButton, Object.assign({ variant: "contained", loading: manageBillingLoading, sx: {
                                    borderRadius: '100px',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    opacity: billingCustomerCreated ? '100%' : '30%',
                                    cursor: billingCustomerCreated ? 'pointer' : 'auto',
                                }, onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    var _g, _h, _j, _k;
                                    try {
                                        // Update loading status
                                        setManageBillingLoading(true);
                                        if (!billingCustomerCreated) {
                                            setManageBillingLoading(false);
                                            return;
                                        }
                                        // Get autheHeader
                                        const authHeader = yield getAuthHeader(user);
                                        const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                                        // Params
                                        const params = {
                                            locale: i18n.language,
                                            returnURL: `${process.env.CLIENT_URL}${CLIENT_ENDPOINTS.SETTINGS}`
                                        };
                                        // Get response
                                        const res = yield axiosClient.post(`${process.env.BILLING_SERVER_URL}${ENDPOINTS.BILLING}${ENDPOINTS.CREATE_PORTAL_SESSION}`, params, { headers });
                                        // Update loading status
                                        setManageBillingLoading(false);
                                        const { sessionURL } = (_g = res === null || res === void 0 ? void 0 : res.data) === null || _g === void 0 ? void 0 : _g.data;
                                        // Redirect to new session url
                                        window.location.href = sessionURL;
                                    }
                                    catch (err) {
                                        // Update loading status
                                        setManageBillingLoading(false);
                                        // Save log
                                        axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                                            level: LOG_LEVELS.ERROR,
                                            logMessage: `Billing, createPortalSession, ${err === null || err === void 0 ? void 0 : err.message}`,
                                            metadata: {
                                                env: process.env.ENV,
                                                service: SERVICES.BILLING,
                                                userId: user === null || user === void 0 ? void 0 : user.uid,
                                                email: user === null || user === void 0 ? void 0 : user.email,
                                                userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                                            },
                                        });
                                        // Check if the error is from axios
                                        if ((_h = err === null || err === void 0 ? void 0 : err.response) === null || _h === void 0 ? void 0 : _h.status) {
                                            // If yes, notify user with the server error message
                                            toast.error((_k = (_j = err === null || err === void 0 ? void 0 : err.response) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.message);
                                            return;
                                        }
                                        ;
                                        // Notify user
                                        toast.error(t('Something went wrong. We are figuring it out.'));
                                    }
                                    ;
                                }) }, { children: t('Update') })) }))] })) })), _jsx(Divider, { sx: billingStyles.divider }), _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { padding: '1.5rem 0 0', color: '#000000' }) }, { children: t('Monthly Usage History') })), _jsx(UsageItem, { usage: (_a = usage === null || usage === void 0 ? void 0 : usage.words) === null || _a === void 0 ? void 0 : _a.basicModels, planMaxUsage: (_b = planMaxUsage.words) === null || _b === void 0 ? void 0 : _b.basicModels, suffix: t('By basic models'), unit: t('words') }), _jsx(UsageItem, { usage: (_c = usage === null || usage === void 0 ? void 0 : usage.words) === null || _c === void 0 ? void 0 : _c.proModels, planMaxUsage: (_d = planMaxUsage.words) === null || _d === void 0 ? void 0 : _d.proModels, suffix: t('By pro models'), unit: t('words') }), _jsx(UsageItem, { usage: (_e = usage === null || usage === void 0 ? void 0 : usage.images) === null || _e === void 0 ? void 0 : _e.proModels, planMaxUsage: (_f = planMaxUsage.images) === null || _f === void 0 ? void 0 : _f.proModels, suffix: t('By all models'), unit: t('images') }), _jsx(Divider, { sx: billingStyles.divider })] })));
};
